import * as React from 'react';
import * as RDropdown from '@radix-ui/react-dropdown-menu';
import { cn } from '../utils/cn.js';

/**
 * DropdownMenu — thin Radix wrapper matching Kollez CSS class contracts.
 * Used by the header workspace switcher (F-Step 104) and future ⌘K commands.
 *
 * a11y: Radix ships focus trap, ESC dismiss, roving arrows, and `role="menu"`. Wrapper does
 * not re-implement those. Styling comes from `kollez.css` — `.menu`, `.menu-item`,
 * `.menu-separator`, `.menu-label`, matching the existing `.palette` shape used by shell.js.
 */

export const DropdownMenu = RDropdown.Root;
export const DropdownMenuTrigger = RDropdown.Trigger;

export interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof RDropdown.Content> {
  className?: string;
}

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof RDropdown.Content>,
  DropdownMenuContentProps
>(function DropdownMenuContent({ className, sideOffset = 6, align = 'start', ...rest }, ref) {
  return (
    <RDropdown.Portal>
      <RDropdown.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        className={cn('menu', className)}
        {...rest}
      />
    </RDropdown.Portal>
  );
});

export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof RDropdown.Item> {
  className?: string;
  danger?: boolean;
}

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof RDropdown.Item>,
  DropdownMenuItemProps
>(function DropdownMenuItem({ className, danger, ...rest }, ref) {
  return (
    <RDropdown.Item
      ref={ref}
      className={cn('menu-item', danger && 'menu-item-danger', className)}
      {...rest}
    />
  );
});

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof RDropdown.Separator>,
  React.ComponentPropsWithoutRef<typeof RDropdown.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return <RDropdown.Separator ref={ref} className={cn('menu-separator', className)} {...rest} />;
});

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof RDropdown.Label>,
  React.ComponentPropsWithoutRef<typeof RDropdown.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return <RDropdown.Label ref={ref} className={cn('menu-label', className)} {...rest} />;
});
